import {Injectable, signal, computed, effect} from '@angular/core';
import {GIRL, ICONS} from "../const/icons";

export const CLASSIC_LICENSE = 'classic_license';
export const UNLIMITED_LICENSE = 'unlimited_license';

export interface User {
  id: string;
  username: string;
  email: string;
  avatar?: string;
  gender?: 0 | 1 | 2;
  license?: {
    valid: boolean;
    expired: boolean;
    type: string;
    startDate?: Date | null;
    endDate?: Date | null;
    space?: number;
  };
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly _user = signal<User | null>(null);
  readonly user = this._user.asReadonly();

  //  Computed helpers
  readonly licenseType = computed(() => this._user()?.license?.type ?? null);
  readonly isLicenseValid = computed(() => !!(this._user()?.license?.valid));
  readonly isLicenseExpired = computed(() => !!(this._user()?.license?.expired));
  readonly hasLicense = computed(() => {
    const user = this._user();
    return !!(user?.license && user.license.type !== null && user.license.type !== undefined); });
  readonly avatarUrl = computed(() => {
    const user = this._user();
    return user?.avatar ?? ICONS.ANONYMOUS_BG_NONE;
  });
  readonly username = computed(() => this._user()?.username ?? 'Guest');
  readonly userId = computed(() => this._user()?.id ?? '');
  readonly gender = computed(() => this._user()?.gender ?? 0);


  constructor() {
    // Load the user from localStorage at startup
    const stored = localStorage.getItem('currentUser');
    if (stored) {
      this._user.set(JSON.parse(stored));
    }

    // Listen to changes from other browser tabs
    window.addEventListener('storage', (event) => {
      if (event.key === 'currentUser' && event.newValue) {
        this._user.set(JSON.parse(event.newValue));
      }
      if (event.key === 'currentUser' && event.newValue === null) {
        this._user.set(null);
      }
    });

    // Effect to automatically update the avatar based on gender
    effect(() => {
      const user = this._user();
      if (!user) return;

      let defaultAvatar = ICONS.ANONYMOUS_BG_NONE; // default avatar

      // Set gender-specific default avatars
      if (user.gender === 1) defaultAvatar = ICONS.BOY_LIGHT_SKIN;
      else if (user.gender === 2) defaultAvatar = GIRL;

      // Update avatar if missing or does not match gender
      if (!user.avatar || this.isAvatarMismatchWithGender(user)) {
        const updated = { ...user, avatar: defaultAvatar };
        this._user.set(updated); // ✅ allowed here in effect
        localStorage.setItem('currentUser', JSON.stringify(updated));
      }
    });
  }



  private isAvatarMismatchWithGender(user: User): boolean {
    if (!user.avatar) return true;

    // Custom avatar (not a default) → never override
    const defaultAvatars = [ICONS.BOY_LIGHT_SKIN, GIRL, ICONS.ANONYMOUS_BG_NONE];
    if (!defaultAvatars.includes(user.avatar)) return false;

    // Default avatar → check if it matches gender
    if (user.gender === 1) return user.avatar !== ICONS.BOY_LIGHT_SKIN;
    if (user.gender === 2) return user.avatar !== GIRL;
    return user.avatar !== ICONS.ANONYMOUS_BG_NONE;
  }

  /**
   * Merge partial data into current user
   * - protects existing fields if partial does not include them
   */
  mergeUser(partial: Partial<User>): void {
    const current = this._user();
    if (!current) return;

    const updated: User = {
      id: current.id,
      username: partial.username ?? current.username,
      email: partial.email ?? current.email,
      avatar: partial.avatar ?? current.avatar,
      gender: partial.gender ?? current.gender,
      license: partial.license ?? current.license,
    };

    if (!this.isEqual(current, updated)) {
      this._user.set(updated);
      this.persistUser(updated);
    }
  }

  setUser(user: User): void {
    const current = this._user();
    if (!current || !this.isEqual(current, user)) {
      this._user.set(user);
      this.persistUser(user);
    }
  }

  clearUser(): void {
    this._user.set(null);
    localStorage.removeItem('currentUser');
  }

  /**
   * Interprète le claim `licence` du token, en gérant les DEUX formats :
   *  - ancien format plat `{ datedebut, datefin, quota }` (dates JJ-MM-AAAA) ;
   *  - nouveau format versionné `{ version, grants: [...] }` (dates ISO).
   * On projette l'abonnement de BASE (grant le plus lointain) vers `User.license`.
   */
  interpretLicense(license: unknown): void {
    const base = this.resolveBaseSubscription(license);
    if (base) {
      this.mergeUser({license: base});
    }
  }

  /** Extrait l'abonnement de base (validité, dates, espace) depuis l'un ou l'autre format. */
  private resolveBaseSubscription(raw: unknown): User['license'] | null {
    if (!raw || typeof raw !== 'object') return null;
    const obj = raw as Record<string, unknown>;

    // Nouveau format à grants.
    if (Array.isArray(obj['grants'])) {
      const grants = obj['grants'] as Record<string, unknown>[];
      const base = grants
        .filter(g => g?.['type'] === 'base')
        .sort((a, b) => this.grantEndMs(b) - this.grantEndMs(a))[0];
      if (!base) return null;

      const startsAt = typeof base['startsAt'] === 'string' ? new Date(base['startsAt']) : null;
      const endsAt = typeof base['endsAt'] === 'string' ? new Date(base['endsAt']) : null;
      const quota = typeof base['quota'] === 'number' ? base['quota'] : 0;
      return this.buildLicense(startsAt, endsAt, quota);
    }

    // Ancien format plat (JJ-MM-AAAA).
    if (typeof obj['datedebut'] === 'string') {
      const startDate = new Date((obj['datedebut']).split('-').reverse().join('-'));
      const endDate = typeof obj['datefin'] === 'string' && obj['datefin']
        ? new Date((obj['datefin']).split('-').reverse().join('-'))
        : null;
      const quota = typeof obj['quota'] === 'number' ? obj['quota'] : 0;
      return this.buildLicense(startDate, endDate, quota);
    }

    return null;
  }

  /** Construit la projection `User.license` à partir des dates de base et du quota. */
  private buildLicense(startDate: Date | null, endDate: Date | null, space: number): User['license'] {
    const today = new Date();
    const type = endDate ? CLASSIC_LICENSE : UNLIMITED_LICENSE;
    const expired = !!((startDate && today < startDate) || (endDate && today > endDate));
    return {valid: !expired, expired, type, startDate, endDate, space};
  }

  /** Fin de validité d'un grant en ms ; null (à vie) = +∞ pour trier le plus lointain en premier. */
  private grantEndMs(grant: Record<string, unknown>): number {
    return typeof grant?.['endsAt'] === 'string'
      ? new Date(grant['endsAt']).getTime()
      : Number.POSITIVE_INFINITY;
  }

  //  Persistence helper
  private persistUser(user: User): void {
    localStorage.setItem('currentUser', JSON.stringify(user));
  }

  //  Equality check
  private isEqual(a: User, b: User): boolean {
    if (a === b) return true;
    if (!a || !b) return false;

    return (
      a.id === b.id &&
      a.username === b.username &&
      a.email === b.email &&
      a.avatar === b.avatar &&
      a.gender === b.gender &&
      this.isLicenseEqual(a.license, b.license)
    );
  }

  private isLicenseEqual(a?: User['license'], b?: User['license']): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a.valid === b.valid
      && a.expired === b.expired
      && a.type === b.type
      && a.space === b.space
      && this.sameTime(a.startDate, b.startDate)
      && this.sameTime(a.endDate, b.endDate);
  }

  /** Égalité de deux dates de licence, tolérante au type (Date ou string relue du localStorage). */
  private sameTime(a?: Date | string | null, b?: Date | string | null): boolean {
    const ta = a ? new Date(a).getTime() : null;
    const tb = b ? new Date(b).getTime() : null;
    return ta === tb;
  }
}
